import { EditType, EditableCellProps } from "@components/EditableCell"
import {
  Distribution,
  DistributionParam,
  GraphNodeType,
  isDistributionParam,
} from "@custom-types/graphs"
import {
  NodeProperties,
  NodeRecord,
  PropertiesRow,
} from "@features/graph/details/components/PropertiesTable"
import { distributionProperties } from "utils/graph"
import { NodeType } from "@features/builder/graph/types"
import { FieldValues } from "react-hook-form"

export type Header = EditableCellProps & {
  key: string
  idx?: number
  value: string
  color: string | undefined
}

export const getPropertiesRows = <
  TFieldValues extends FieldValues,
  TNodeRecord extends NodeRecord,
  TSampleValue,
>(
  properties: NodeProperties<TNodeRecord, TSampleValue>,
  labelMap: Record<string, string>,
  nodeTypeMap: Record<string, NodeType | GraphNodeType>,
  type: GraphNodeType | NodeType,
  onBlur?: (field: string, newValue: string, index: number) => void,
  editable?: boolean,
): PropertiesRow<TFieldValues>[] => {
  const parentNodeIds = properties["parents_lst"]
  const rows: PropertiesRow<TFieldValues>[] = []
  const distributions = properties.df_cpd.distribution ?? []

  distributions.forEach((_val, i) => {
    Object.entries(properties.df_cpd).forEach(([key, values]) => {
      if (!rows[i]) {
        rows[i] = {}
      }

      const distribution = distributions[i] as Distribution
      const isCellEditable =
        editable && isPropertyEditable(key, distribution, parentNodeIds, type, nodeTypeMap)
      const value = values?.[i] ?? ""

      rows[i][key] = {
        sx: parentNodeIds.includes(key) ? { backgroundColor: "#bbd0e8" } : undefined,
        children:
          typeof value === "string" && parentNodeIds.includes(value) ? labelMap[value] : value,
        value,
        editable: isCellEditable,
        markNonEditable: editable,
      }

      if (isCellEditable) {
        rows[i][key].options = parentNodeIds.map((id) => ({ label: labelMap[id] ?? "", value: id }))
        rows[i][key].type = getCellEditType(key, nodeTypeMap, type)
        rows[i][key].onBlur = (newValue) => onBlur?.(key, newValue, i)
      }
    })
  })

  return rows
}

const getCellEditType = (
  cellKey: string,
  nodeTypeMap: Record<string, NodeType | GraphNodeType>,
  type: GraphNodeType | NodeType,
): EditType => {
  const parentNodeType = nodeTypeMap[cellKey] as GraphNodeType | undefined

  if (!parentNodeType) return EditType.Number

  switch (type) {
    case GraphNodeType.CONTINOUS:
    case GraphNodeType.MIXTURE:
      return EditType.Number
  }

  if (
    [GraphNodeType.LAG, GraphNodeType.FORECAST, GraphNodeType.DISCRETE].includes(parentNodeType)
  ) {
    return EditType.Number
  }

  return EditType.NumberOrSingleOption
}

export const getPropertiesHeaders = <TNodeRecord extends NodeRecord, TSampleValue = string>(
  properties: NodeProperties<TNodeRecord, TSampleValue>,
  labelMap: Record<string, string>,
): Header[] => {
  const paramKeys = Object.keys(properties.df_cpd)

  // only parents that are referenced in df_cpd, others should not be present in the table
  const parentNodeNames = properties["parents_lst"].filter((parentId) =>
    paramKeys.includes(parentId),
  )
  const params = paramKeys.filter(
    (param) => param !== "distribution" && !parentNodeNames.includes(param),
  )

  const orderedParams = params.toSorted((paramA, paramB) => {
    // may be categorical param defined by user - keep them in order as they are defined
    if (!isDistributionParam(paramA) || !isDistributionParam(paramB)) return 0

    return DISTRIBUTION_PARAMS_ORDER_MAP[paramA] - DISTRIBUTION_PARAMS_ORDER_MAP[paramB]
  })

  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  return [
    ...parentNodeNames.map((name) => ({ key: name, value: labelMap[name], color: "#bbd0e8" })),
    { key: "distribution", value: "Distribution", color: undefined },
    ...orderedParams.map((param) => ({ key: param, value: param, color: undefined })),
  ]
}

const isPropertyEditable = (
  key: string,
  distribution: Distribution,
  parentNodeIds: string[],
  type: GraphNodeType | NodeType,
  nodeTypeMap: Record<string, NodeType | GraphNodeType>,
): boolean => {
  const isParentProperty = parentNodeIds.includes(key)
  const parentNodeType = nodeTypeMap[key] as GraphNodeType | undefined

  if (isParentProperty) {
    if (!parentNodeType) return false

    switch (type) {
      case GraphNodeType.TABLE:
        return true
      case GraphNodeType.CATEGORICAL:
      case GraphNodeType.DISCRETE:
        return false
      case GraphNodeType.CONTINOUS:
        return [
          GraphNodeType.CONTINOUS,
          GraphNodeType.DETERMINISTIC,
          GraphNodeType.MIXTURE,
          GraphNodeType.LAG,
          GraphNodeType.FORECAST,
          GraphNodeType.SCENARIO,
          GraphNodeType.DISCRETE,
        ].includes(parentNodeType)
      case GraphNodeType.MIXTURE:
        return [
          GraphNodeType.MIXTURE,
          GraphNodeType.CONTINOUS,
          GraphNodeType.DETERMINISTIC,
          GraphNodeType.FORECAST,
          GraphNodeType.SCENARIO,
          GraphNodeType.LAG,
        ].includes(parentNodeType)
      default:
        return false
    }
  }

  if (key === "distribution") {
    return false
  }

  // categorical distribution have params defined by user - allow to modify them
  if (distribution === Distribution.Categorical) {
    return true
  }

  return (distributionProperties[distribution] ?? []).includes(key as DistributionParam)
}

const DISTRIBUTION_PARAMS_ORDER_MAP: Record<DistributionParam, number> = {
  [DistributionParam.loc]: 1,
  [DistributionParam.scale]: 2,
  [DistributionParam.n]: 3,
  [DistributionParam.p]: 4,
  [DistributionParam.c]: 5,
  [DistributionParam.kappa]: 6,
  [DistributionParam.df]: 7,
  [DistributionParam.a]: 8,
  [DistributionParam.b]: 9,
  [DistributionParam.dfn]: 10,
  [DistributionParam.dfd]: 11,
  [DistributionParam.nc]: 12,
  [DistributionParam.s]: 13,
  [DistributionParam.mu]: 14,
  [DistributionParam.N]: 15,
  [DistributionParam.M]: 16,
  [DistributionParam.lbound]: 98,
  [DistributionParam.ubound]: 99,
}
